import React, { useState, useEffect, useMemo } from 'react';
import { DocumentTextIcon, PencilIcon, TrashIcon, PlusIcon, PlayIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import Card from '../Card';
import Table from '../Table';
import Modal from '../Modal';

// Парсинг переменных из текста шаблона
const parseVariables = (content) => {
  const matches = content?.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
};

const PromptTemplatesSection = ({ api, onRefresh }) => {
  const [templates, setTemplates] = useState([]);
  const [globalVars, setGlobalVars] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showGlobalVarModal, setShowGlobalVarModal] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [editingGlobalVar, setEditingGlobalVar] = useState(null);
  
  // Форма: content + varLabels + varTypes + varOptions
  const [form, setForm] = useState({ name: '', content: '', analysis_prompt: '', varLabels: {}, varTypes: {}, varOptions: {} });
  const [globalVarForm, setGlobalVarForm] = useState({ name: '', default_value: '', description: '', var_type: 'text', options: [] });
  
  // Тестирование
  const [testVars, setTestVars] = useState({});
  const [testMessage, setTestMessage] = useState('Привет!');
  const [testResponse, setTestResponse] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesData, globalVarsData] = await Promise.all([
        api.fetchPromptTemplates(),
        fetch('/api/prompts/global-variables', { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }).then(r => r.json())
      ]);
      setTemplates(templatesData.filter(t => t.id !== '__builtin__'));
      setGlobalVars(globalVarsData || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Глобальные переменные CRUD
  const handleAddGlobalVar = () => {
    setEditingGlobalVar(null);
    setGlobalVarForm({ name: '', default_value: '', description: '', var_type: 'text', options: [] });
    setShowGlobalVarModal(true);
  };

  const handleEditGlobalVar = (v) => {
    setEditingGlobalVar(v);
    setGlobalVarForm({ name: v.name, default_value: v.default_value || '', description: v.description || '', var_type: v.var_type || 'text', options: v.options || [] });
    setShowGlobalVarModal(true);
  };

  const handleSaveGlobalVar = async () => {
    const token = localStorage.getItem('access_token');
    const payload = { ...globalVarForm, options: globalVarForm.var_type === 'select' ? globalVarForm.options : null };
    try {
      if (editingGlobalVar) {
        await fetch(`/api/prompts/global-variables/${editingGlobalVar.id}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        toast.success('Переменная обновлена');
      } else {
        await fetch('/api/prompts/global-variables', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        toast.success('Переменная создана');
      }
      setShowGlobalVarModal(false);
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteGlobalVar = async (id) => {
    if (!window.confirm('Удалить переменную?')) return;
    const token = localStorage.getItem('access_token');
    await fetch(`/api/prompts/global-variables/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    toast.success('Удалено');
    loadData();
  };

  // Автопарсинг переменных из текущего контента формы (content + analysis_prompt)
  const parsedVars = useMemo(() => {
    const fromContent = parseVariables(form.content);
    const fromAnalysis = parseVariables(form.analysis_prompt);
    const allVars = [...new Set([...fromContent, ...fromAnalysis])];
    // Исключаем глобальные переменные
    const globalVarNames = globalVars.map(v => v.name);
    return allVars.filter(v => !globalVarNames.includes(v));
  }, [form.content, form.analysis_prompt, globalVars]);

  const handleAdd = () => {
    setForm({ name: '', content: '', analysis_prompt: '', varLabels: {}, varTypes: {}, varOptions: {} });
    setShowAddModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createPromptTemplate({ 
        name: form.name, 
        content: form.content, 
        analysis_prompt: form.analysis_prompt,
        var_labels: form.varLabels,
        var_types: form.varTypes,
        var_options: form.varOptions
      });
      toast.success('Шаблон создан');
      setShowAddModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (template) => {
    setCurrentTemplate(template);
    setForm({ 
      name: template.name || '', 
      content: template.content || '', 
      analysis_prompt: template.analysis_prompt || '',
      varLabels: template.var_labels || {},
      varTypes: template.var_types || {},
      varOptions: template.var_options || {}
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updatePromptTemplate(currentTemplate.id, { 
        name: form.name, 
        content: form.content, 
        analysis_prompt: form.analysis_prompt,
        var_labels: form.varLabels,
        var_types: form.varTypes,
        var_options: form.varOptions
      });
      toast.success('Шаблон обновлен');
      setShowEditModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Удалить шаблон?')) return;
    try {
      await api.deletePromptTemplate(templateId);
      toast.success('Шаблон удален');
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Открыть тестирование
  const openTest = (template) => {
    setCurrentTemplate(template);
    const vars = parseVariables(template.content);
    setTestVars(Object.fromEntries(vars.map(v => [v, ''])));
    setTestMessage('Привет!');
    setTestResponse('');
    setShowTestModal(true);
  };

  // Собрать финальный промпт
  const buildPrompt = (content, vars) => {
    let result = content;
    Object.entries(vars).forEach(([k, v]) => {
      result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v || `[${k}]`);
    });
    return result;
  };

  // Тестовый запрос к ИИ
  const handleTest = async () => {
    setTesting(true);
    setTestResponse('');
    try {
      const systemPrompt = buildPrompt(currentTemplate.content, testVars);
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/prompts/test', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ system_prompt: systemPrompt, user_message: testMessage })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Ошибка');
      setTestResponse(data.response);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTesting(false);
    }
  };

  const testVarsArray = useMemo(() => parseVariables(currentTemplate?.content), [currentTemplate]);

  // Получить label для переменной
  const getVarLabel = (template, varName) => template?.var_labels?.[varName] || varName;

  const handleSetDefault = async (id, isDefault) => {
    const token = localStorage.getItem('access_token');
    try {
      await fetch(`/api/prompts/templates/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_default: isDefault })
      });
      toast.success(isDefault ? 'Шаблон установлен по умолчанию' : 'Флаг снят');
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const columns = [
    { header: 'Название', render: (row) => (
      <div className="flex items-center gap-2">
        <span className="text-gray-900 dark:text-gray-100 font-medium">{row.name}</span>
        {row.is_default && <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded">По умолчанию</span>}
      </div>
    )},
    { header: 'Переменные', render: (row) => {
      const vars = parseVariables(row.content);
      return vars.length ? (
        <span className="text-xs text-purple-600">
          {vars.map(v => row.var_labels?.[v] || v).join(', ')}
        </span>
      ) : <span className="text-gray-400">—</span>;
    }},
    { header: 'Действия', render: (row) => (
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => handleSetDefault(row.id, !row.is_default)} className={`${row.is_default ? 'bg-gray-500' : 'bg-yellow-500'} text-white rounded px-2 py-1 text-xs hover:opacity-80`}>
          {row.is_default ? 'Снять' : 'По умолч.'}
        </button>
        <button onClick={() => openTest(row)} className="bg-green-600 text-white rounded px-2 py-1 text-xs hover:bg-green-700 flex items-center">
          <PlayIcon className="w-3 h-3 mr-1" />Тест
        </button>
        <button onClick={() => handleEdit(row)} className="bg-blue-600 text-white rounded px-2 py-1 text-xs hover:bg-blue-700 flex items-center">
          <PencilIcon className="w-3 h-3 mr-1" />Ред.
        </button>
        <button onClick={() => handleDelete(row.id)} className="bg-red-600 text-white rounded px-2 py-1 text-xs hover:bg-red-700 flex items-center">
          <TrashIcon className="w-3 h-3 mr-1" />Удал.
        </button>
      </div>
    )}
  ];

  return (
    <>
      {/* Глобальные переменные */}
      <Card title="Глобальные переменные" icon={GlobeAltIcon} headerActions={
        <button onClick={handleAddGlobalVar} className="bg-green-500 text-white rounded-lg px-3 py-1 hover:bg-green-700 text-xs sm:text-base flex items-center">
          <PlusIcon className="w-4 h-4 mr-1" />Добавить
        </button>
      } className="mb-6">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Глобальные переменные доступны во всех шаблонах через <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{'{{имя}}'}</code>
        </p>
        {globalVars.length === 0 ? (
          <p className="text-gray-500">Нет глобальных переменных</p>
        ) : (
          <div className="grid gap-2">
            {globalVars.map(v => (
              <div key={v.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <code className="text-purple-600 dark:text-purple-400 font-mono">{`{{${v.name}}}`}</code>
                  <span className="text-gray-500 ml-2 text-sm">{v.description}</span>
                  {v.var_type === 'select' && <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">select</span>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEditGlobalVar(v)} className="text-blue-600 hover:text-blue-800"><PencilIcon className="w-4 h-4" /></button>
                  <button onClick={() => handleDeleteGlobalVar(v.id)} className="text-red-600 hover:text-red-800"><TrashIcon className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Шаблоны промптов" icon={DocumentTextIcon} headerActions={
        <button onClick={handleAdd} className="bg-green-500 text-white rounded-lg px-3 py-1 hover:bg-green-700 text-xs sm:text-base flex items-center">
          <PlusIcon className="w-4 h-4 mr-1" />Добавить
        </button>
      }>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Используйте <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{'{{переменная}}'}</code> в тексте — они автоматически появятся для настройки.
        </p>
        <Table columns={columns} data={templates} loading={loading} emptyMessage="Нет шаблонов" />
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={showAddModal || showEditModal} onClose={() => { setShowAddModal(false); setShowEditModal(false); }} title={showEditModal ? `Редактировать: ${currentTemplate?.name}` : 'Создать шаблон'} size="2xl">
        <form onSubmit={showEditModal ? handleUpdate : handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Название шаблона</label>
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full h-12 px-4 rounded-xl border dark:bg-gray-700 dark:text-gray-100" placeholder="Например: Продажи B2B" required />
          </div>
          
          {/* Доступные глобальные переменные */}
          {globalVars.length > 0 && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2 text-sm">Глобальные переменные (можно использовать в промптах):</h4>
              <div className="flex flex-wrap gap-2">
                {globalVars.map(v => (
                  <span key={v.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-800 rounded text-xs cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-700" onClick={() => navigator.clipboard.writeText(`{{${v.name}}}`)} title={`${v.description || v.name}${v.var_type === 'select' ? ' (select)' : ''}\nКлик - скопировать`}>
                    <code className="text-blue-700 dark:text-blue-300">{`{{${v.name}}}`}</code>
                    {v.var_type === 'select' && <span className="text-blue-500 text-[10px]">(select)</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Системный промпт</label>
            <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} className="w-full min-h-40 px-4 py-3 rounded-xl border dark:bg-gray-700 dark:text-gray-100 resize-y font-mono text-sm" placeholder="Ты - {{role}}. Ситуация: {{situation}}. Веди себя {{mood}}." required />
          </div>
          
          {/* Настройка имён переменных */}
          {parsedVars.length > 0 && (
            <div className="p-4 bg-purple-50 dark:bg-purple-900/30 rounded-xl">
              <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-3">Настройка переменных:</h4>
              <div className="space-y-4">
                {parsedVars.map(v => (
                  <div key={v} className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700">
                    <div className="flex items-center gap-3 mb-2">
                      <code className="text-purple-600 dark:text-purple-400 text-sm font-mono">{`{{${v}}}`}</code>
                      <input 
                        type="text" 
                        value={form.varLabels[v] || ''} 
                        onChange={e => setForm(p => ({ ...p, varLabels: { ...p.varLabels, [v]: e.target.value } }))} 
                        className="flex-1 px-3 py-1.5 rounded-lg border dark:bg-gray-700 dark:text-gray-100 text-sm" 
                        placeholder="Название переменной" 
                      />
                      <select
                        value={form.varTypes[v] || 'text'}
                        onChange={e => setForm(p => ({ ...p, varTypes: { ...p.varTypes, [v]: e.target.value } }))}
                        className="px-2 py-1.5 rounded-lg border dark:bg-gray-700 dark:text-gray-100 text-sm"
                      >
                        <option value="text">Текст</option>
                        <option value="select">Выбор</option>
                      </select>
                    </div>
                    {form.varTypes[v] === 'select' && (
                      <div className="mt-2">
                        <label className="text-xs text-purple-600 dark:text-purple-400 mb-1 block">Варианты (по одному на строку):</label>
                        <textarea
                          value={(form.varOptions[v] || []).join('\n')}
                          onChange={e => setForm(p => ({ ...p, varOptions: { ...p.varOptions, [v]: e.target.value.split('\n') } }))}
                          onBlur={() => setForm(p => ({ ...p, varOptions: { ...p.varOptions, [v]: (p.varOptions[v] || []).filter(Boolean) } }))}
                          className="w-full h-20 px-3 py-2 rounded-lg border dark:bg-gray-700 dark:text-gray-100 text-sm resize-none"
                          placeholder="Вариант 1&#10;Вариант 2&#10;Вариант 3"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Промпт для анализа (опционально)</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Можно использовать те же переменные: {`{{переменная}}`}</p>
            <textarea value={form.analysis_prompt} onChange={e => setForm(p => ({ ...p, analysis_prompt: e.target.value }))} className="w-full min-h-24 px-4 py-3 rounded-xl border dark:bg-gray-700 dark:text-gray-100 resize-y font-mono text-sm" placeholder="Проанализируй диалог с учётом {{company_name}}..." />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="flex-1 bg-gray-300 dark:bg-gray-600 py-2 rounded-xl">Отмена</button>
            <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700">{showEditModal ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </Modal>

      {/* Test Modal */}
      <Modal isOpen={showTestModal} onClose={() => setShowTestModal(false)} title={`Тест: ${currentTemplate?.name}`} size="xl">
        <div className="space-y-4">
          {/* Переменные с человекочитаемыми именами */}
          {testVarsArray.length > 0 && (
            <div>
              <h4 className="font-medium mb-2 text-gray-700 dark:text-gray-300">Заполните переменные:</h4>
              <div className="grid gap-2">
                {testVarsArray.map(v => (
                  <div key={v}>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {getVarLabel(currentTemplate, v)}
                      <code className="text-purple-500 text-xs ml-2">{`{{${v}}}`}</code>
                    </label>
                    <input type="text" value={testVars[v] || ''} onChange={e => setTestVars(p => ({ ...p, [v]: e.target.value }))} className="w-full px-3 py-2 rounded-lg border dark:bg-gray-700 dark:text-gray-100 text-sm" placeholder={`Введите ${getVarLabel(currentTemplate, v).toLowerCase()}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Превью промпта */}
          <div>
            <h4 className="font-medium mb-2 text-gray-700 dark:text-gray-300">Итоговый системный промпт:</h4>
            <pre className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs overflow-auto max-h-32 whitespace-pre-wrap">{buildPrompt(currentTemplate?.content || '', testVars)}</pre>
          </div>

          {/* Тестовое сообщение */}
          <div>
            <h4 className="font-medium mb-2 text-gray-700 dark:text-gray-300">Сообщение пользователя:</h4>
            <input type="text" value={testMessage} onChange={e => setTestMessage(e.target.value)} className="w-full px-4 py-3 rounded-xl border dark:bg-gray-700 dark:text-gray-100" placeholder="Введите сообщение..." />
          </div>

          <button onClick={handleTest} disabled={testing} className="w-full bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center justify-center">
            <PlayIcon className="w-5 h-5 mr-2" />
            {testing ? 'Отправка...' : 'Отправить тестовый запрос'}
          </button>

          {/* Ответ ИИ */}
          {testResponse && (
            <div>
              <h4 className="font-medium mb-2 text-gray-700 dark:text-gray-300">Ответ ИИ:</h4>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{testResponse}</div>
            </div>
          )}
        </div>
      </Modal>

      {/* Global Variable Modal */}
      <Modal isOpen={showGlobalVarModal} onClose={() => setShowGlobalVarModal(false)} title={editingGlobalVar ? 'Редактировать переменную' : 'Новая глобальная переменная'}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Имя переменной</label>
            <input type="text" value={globalVarForm.name} onChange={e => setGlobalVarForm(p => ({ ...p, name: e.target.value.replace(/\s/g, '_') }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-700 dark:text-gray-100" placeholder="company_name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Описание</label>
            <input type="text" value={globalVarForm.description} onChange={e => setGlobalVarForm(p => ({ ...p, description: e.target.value }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-700 dark:text-gray-100" placeholder="Название компании" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Тип</label>
            <select value={globalVarForm.var_type} onChange={e => setGlobalVarForm(p => ({ ...p, var_type: e.target.value }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-700 dark:text-gray-100">
              <option value="text">Текст</option>
              <option value="select">Выбор из списка</option>
            </select>
          </div>
          {globalVarForm.var_type === 'select' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Варианты (по одному на строку)</label>
              <textarea 
                value={(globalVarForm.options || []).join('\n')} 
                onChange={e => setGlobalVarForm(p => ({ ...p, options: e.target.value.split('\n') }))} 
                onBlur={e => setGlobalVarForm(p => ({ ...p, options: p.options.filter(Boolean) }))}
                className="w-full h-24 px-4 py-2 rounded-xl border dark:bg-gray-700 dark:text-gray-100 resize-y" 
                placeholder="Вариант 1&#10;Вариант 2&#10;Вариант 3" 
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Значение по умолчанию</label>
            {globalVarForm.var_type === 'select' && globalVarForm.options?.length > 0 ? (
              <select value={globalVarForm.default_value} onChange={e => setGlobalVarForm(p => ({ ...p, default_value: e.target.value }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-700 dark:text-gray-100">
                <option value="">— Выберите —</option>
                {globalVarForm.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : (
              <input type="text" value={globalVarForm.default_value} onChange={e => setGlobalVarForm(p => ({ ...p, default_value: e.target.value }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-700 dark:text-gray-100" />
            )}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setShowGlobalVarModal(false)} className="flex-1 bg-gray-300 dark:bg-gray-600 py-2 rounded-xl">Отмена</button>
            <button type="button" onClick={handleSaveGlobalVar} className="flex-1 bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700">{editingGlobalVar ? 'Сохранить' : 'Создать'}</button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PromptTemplatesSection;
